import { ReactNode } from 'react';
import { useStudioPlan } from '../hooks/useStudioPlan';
import { BooleanCapabilityKey } from '../plans/types';
import UpgradePrompt from './UpgradePrompt';

interface GatedPageLayoutProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}

export function GatedPageLayout({
  title,
  subtitle,
  children,
  className = '',
}: GatedPageLayoutProps) {
  return (
    <div className={`relative bg-gray-50 dark:bg-gray-900 ${className}`.trim()}>
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6 min-w-0 max-w-full">
        {/* Placeholder so space-y-4 header offset matches pages with Notification */}
        <div aria-hidden="true" className="h-0" />
        <div className="flex flex-col gap-4">
          {title && (
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}

interface PlanGatedPageProps {
  capability: BooleanCapabilityKey;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** @deprecated Layout is standardized; extra classes rarely needed */
  gatedClassName?: string;
}

export default function PlanGatedPage({
  capability,
  title,
  subtitle,
  children,
  gatedClassName = '',
}: PlanGatedPageProps) {
  const { hasCapability, loading } = useStudioPlan();

  if (loading) {
    return (
      <GatedPageLayout className={gatedClassName}>
        <div className="flex justify-center py-16">
          <div className="animate-pulse flex space-x-4 items-center">
            <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
            <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
        </div>
      </GatedPageLayout>
    );
  }

  if (!hasCapability(capability)) {
    return (
      <GatedPageLayout title={title} subtitle={subtitle} className={gatedClassName}>
        <UpgradePrompt capability={capability} hideTitle={!!title} />
      </GatedPageLayout>
    );
  }

  return <>{children}</>;
}
